import React, { useState, useRef, useEffect } from "react";
import { Form, Input, Button as AntdButton, Card as AntdCard, Typography } from "antd";
import { Mail, ArrowLeft, CheckCircle, LockKeyhole, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { sendResetEmail, verifyOtp, resetPassword } from "../services/authService";

const { Title, Paragraph } = Typography;

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60; // seconds

export default function ForgotPassword() {
  const [step, setStep] = useState("forgot"); // 'forgot' | 'verify' | 'reset' 
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // --- email / api states
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // --- OTP state
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const otpRefs = useRef([]);
  const [resendTimer, setResendTimer] = useState(0);

  // Timer countdown effect
  useEffect(() => {
    let t;
    if (resendTimer > 0) {
      t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    }
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Debug resetToken changes
  // useEffect(() => {
  //   console.log('🔄 Reset token state changed to:', resetToken);
  // }, [resetToken]);

  // API calls using axios service
  const apiSendResetEmail = sendResetEmail;
  const apiVerifyOtp = verifyOtp;
  const apiResetPassword = resetPassword;

  // ---- actions
  const handleSend = async (values) => {
    setIsSending(true);
    try {
      const emailAddr = values.email.trim();
      const res = await apiSendResetEmail(emailAddr);
      if (res.ok) {
        setEmail(emailAddr);
        setStep("verify");
        setResendTimer(RESEND_COOLDOWN);
        // clear otp fields
        setOtp(Array(OTP_LENGTH).fill(""));
        // focus first otp after a tick
        setTimeout(() => otpRefs.current[0]?.focus(), 200);
        toast.success("Reset email sent successfully!");
      } else {
        // handle error with toast
        let errorMessage = "Failed to send reset email";
        
        if (res.status === 422) {
          errorMessage = "Invalid email format or email not found";
        } else if (res.error) {
          if (typeof res.error === 'string') {
            errorMessage = res.error;
          } else if (res.error.message) {
            errorMessage = res.error.message;
          } else if (res.error.detail) {
            errorMessage = res.error.detail;
          }
        }
        
        // console.log('🚨 Error details:', res);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred. Please try again.");
    }
    setIsSending(false);
  };

  const handleChangeOtp = (value, idx) => {
    if (!/^[0-9]?$/.test(value)) return; // allow only single digit numeric (or empty)
    const next = [...otp];
    next[idx] = value;
    setOtp(next);
    if (value && idx < OTP_LENGTH - 1) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      const prev = otpRefs.current[idx - 1];
      prev?.focus();
      const next = [...otp];
      next[idx - 1] = "";
      setOtp(next);
    } else if (e.key === "ArrowLeft" && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length !== OTP_LENGTH) return;
    setIsVerifying(true);
    try {
      const res = await apiVerifyOtp(email, code);
      // console.log('🔐 OTP verification result:', res);

      if (res.ok) {
        // prefer res.reset_token (provided by service), fall back to nested spots
        const token =
          res.reset_token ||
          (res.data && (res.data.reset_token || res.data.token)) ||
          (res.data && res.data.data && (res.data.data.reset_token || res.data.data.token)) ||
          "";

        // console.log('🔐 Extracted reset token:', token);
        setResetToken(token);
        setStep("reset");
        toast.success("OTP verified successfully!");
      } else {
        // invalid code UI: reset inputs + show error
        setOtp(Array(OTP_LENGTH).fill(""));
        otpRefs.current[0]?.focus();
        
        let errorMessage = "Invalid verification code";
        if (res.error) {
          if (typeof res.error === 'string') {
            errorMessage = res.error;
          } else if (res.error.message) {
            errorMessage = res.error.message;
          }
        }
        
        // console.log('🚨 OTP verification error:', res);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setIsSending(true);
    try {
      const res = await apiSendResetEmail(email || form.getFieldValue("email"));
      if (res.ok) {
        setResendTimer(RESEND_COOLDOWN);
        setOtp(Array(OTP_LENGTH).fill(""));
        otpRefs.current[0]?.focus();
        toast.success("Reset email sent successfully!");
      } else {
        let errorMessage = "Failed to resend reset email";
        
        if (res.status === 422) {
          errorMessage = "Invalid email format or email not found";
        } else if (res.error) {
          if (typeof res.error === 'string') {
            errorMessage = res.error;
          } else if (res.error.message) {
            errorMessage = res.error.message;
          } else if (res.error.detail) {
            errorMessage = res.error.detail;
          }
        }
        
        // console.log('🚨 Resend error details:', res);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred. Please try again.");
    }
    setIsSending(false);
  };

  const handleReset = async (values) => {
    const { password, confirm } = values;
    // basic checks first
    if (!password || !confirm) {
      toast.error("Please fill both password fields.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
  
    // local policy check (prevents server 400)
    // const localCheck = validatePasswordLocally(password);
    // if (!localCheck.ok) {
    //   toast.error(localCheck.message);
    //   return;
    // }
  
    if (!resetToken) {
      toast.error("Reset token not found. Please verify your OTP again.");
      return;
    }
  
    setIsResetting(true);
    try {
      // pass email, new password, reset token (your service builds query params)
      const res = await apiResetPassword(email, password, resetToken);
      // console.log("reset result:", res);
  
      if (res.ok) {
        toast.success("Successfully reset password!");
        setResetToken("");
        setTimeout(() => navigate("/login"), 1500);
      } else {
        // Use server message if present for the user — it's authoritative
        const serverMsg =
          (res.error && (typeof res.error === "string" ? res.error : res.error.message)) ||
          res.message ||
          "Failed to reset password";
        // console.log("🚨 Password reset error:", res);
        toast.error(serverMsg);
      }
    } catch (err) {
      console.error("Unexpected reset error:", err);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsResetting(false);
    }
  };

  const renderTopIcon = () => {
    if (step === "verify") {
      return (
        <div className="w-20 h-20 bg-slate-800/60 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-white/5">
          <ShieldCheck className="w-8 h-8 text-cyan-400" />
        </div>
      );
    }
    if (step === "reset") {
      return (
        <div className="w-20 h-20 bg-slate-800/60 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-white/5">
          <CheckCircle className="w-8 h-8 text-cyan-400" />
        </div>
      );
    }
    // forgot or default
    return (
      <div className="w-20 h-20 bg-slate-800/60 rounded-full flex items-center justify-center mx-auto mb-4 ring-1 ring-white/5">
        <Mail className="w-8 h-8 text-cyan-400" />
      </div>
    );
  };

  // ------ UI blocks per step
  const forgotBlock = (
    <>
      <div className="text-center mb-8">
        {renderTopIcon()}
        <Title
          level={2}
          className="!m-0 !text-3xl !font-semibold !text-white mb-2"
        >
          Forgot Password
        </Title>
        <Paragraph className="!m-0 text-gray-300">
          Enter your email to receive a verification code
        </Paragraph>
      </div>


      <Form
        form={form}
        layout="vertical"
        onFinish={handleSend}
        className="space-y-6"
        initialValues={{ email: "" }}
      >
        {/* Email */}
        <Form.Item
          label={<span className="text-gray-200 text-base">Email Address</span>}
          name="email"
          rules={[
            { required: true, message: "Please enter your email" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            type="email"
            placeholder="Enter your email"
            prefix={<Mail className="w-5 h-5 text-gray-500" />}
            size="large"
            className="h-12 bg-gray-50 border-gray-200 rounded-lg"
          />
        </Form.Item>

        {/* Send Reset Button */}
        <Form.Item>
          <AntdButton
            htmlType="submit"
            type="primary"
            size="large"
            loading={isSending}
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Send
          </AntdButton>
        </Form.Item>
      </Form>

      <div className="mt-6 text-center">
        <AntdButton
          type="text"
          size="large"
          onClick={() => navigate("/login")}
          className="text-gray-300 hover:!text-gray-200 font-medium transition-all duration-200"
        >
          <span className="inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </span>
        </AntdButton>
      </div>
    </>
  );

  const verifyBlock = (
    <>
      <div className="text-center mb-6">
        {renderTopIcon()}
        <Title
          level={2}
          className="!m-0 !text-3xl !font-semibold !text-white mb-2"
        >
          Verification
        </Title>
        <Paragraph className="!m-0 text-gray-300">
          We've sent a verification code to
        </Paragraph>
        <Paragraph className="!m-0 text-cyan-400 font-medium text-base mt-2 break-words">
          {email}
        </Paragraph>
      </div>

      <div className="mt-6">
        <div className="text-gray-100 mb-3 font-medium">
          Enter Verification Code
        </div>

        <div className="flex items-center justify-center gap-4 mb-6">
          {Array.from({ length: OTP_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => (otpRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={otp[i]}
              onChange={(e) =>
                handleChangeOtp(e.target.value.replace(/\D/g, ""), i)
              }
              onKeyDown={(e) => handleOtpKeyDown(e, i)}
              className="w-14 h-14 rounded-xl bg-transparent border border-white/10 text-center text-lg text-white placeholder-white/30 outline-none focus:border-cyan-400"
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        <div className="text-center text-sm text-gray-300 mb-4">
          If you didn't receive a code,{" "}
          <button
            className={`ml-1 font-medium underline ${
              resendTimer > 0
                ? "opacity-50 cursor-not-allowed"
                : "text-cyan-400"
            }`}
            onClick={handleResend}
            disabled={resendTimer > 0}
            aria-disabled={resendTimer > 0}
          >
            Resend
          </button>
          {resendTimer > 0 && (
            <span className="text-gray-400 ml-2">({resendTimer}s)</span>
          )}
        </div>

        <AntdButton
          onClick={handleVerify}
          type="primary"
          size="large"
          loading={isVerifying}
          disabled={otp.join("").length !== OTP_LENGTH}
          className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg transition-all duration-200"
        >
          Send
        </AntdButton>

        <div className="mt-6 text-center">
          <AntdButton
            type="text"
            size="large"
            onClick={() => {
              setStep("forgot");
              form.resetFields();
              setResetToken(""); // Clear reset token when going back
            }}
            className="text-gray-300 hover:!text-gray-200 font-medium transition-all duration-200"
          >
            <span className="inline-flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Email
            </span>
          </AntdButton>
        </div>
      </div>
    </>
  );

  const resetBlock = (
    <>
      <div className="text-center mb-6">
        {renderTopIcon()}
        <Title
          level={2}
          className="!m-0 !text-3xl !font-semibold !text-white mb-2"
        >
          New Password
        </Title>
        <Paragraph className="!m-0 text-gray-300">
          Create a strong password for your account
        </Paragraph>
      </div>

      <Form layout="vertical" onFinish={handleReset}>

        <Form.Item
          name="password"
          label={
            <span className="text-gray-200 font-medium text-base">
              Enter New Password
            </span>
          }
          rules={[
            { required: true, message: "Please input your password!" },
            {
              min: 8,
              max: 15,
              message: "Password must be between 8 and 15 characters long!",
            },
            {
              pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*]).+$/,
              message:
                "Password must contain at least 1 uppercase letters, 1 special characters, 1 numbers, and 1 lowercase letters!",
            },
          ]}
        >
          <Input.Password
            prefix={<LockKeyhole className="text-gray-500" />}
            placeholder="Password"
            size="large"
            className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
          />
        </Form.Item>
        <Form.Item
          name="confirm"
          label={
            <span className="text-gray-200 font-medium text-base">
              Confirm Password
            </span>
          }
          dependencies={["password"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: "Please confirm your password!",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("The two passwords that you entered do not match!")
                );
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockKeyhole className="text-gray-500" />}
            placeholder="Confirm Password"
            size="large"
            className="h-auto min-h-[40px] bg-gray-50 border-gray-200 rounded-lg"
          />
        </Form.Item>

        <Form.Item>
          <AntdButton
            htmlType="submit"
            type="primary"
            size="large"
            loading={isResetting}
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-xl shadow-lg transition-all duration-200"
          >
            Send
          </AntdButton>
        </Form.Item>
      </Form>

      <div className="mt-2 text-center">
        <AntdButton
          type="text"
          size="large"
          onClick={() => {
            setStep("verify");
            setOtp(Array(OTP_LENGTH).fill(""));
            setResetToken(""); // Clear reset token when going back
            otpRefs.current[0]?.focus();
          }}
          className="text-gray-300 hover:!text-gray-200 font-medium transition-all duration-200"
        >
          <span className="inline-flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Verification
          </span>
        </AntdButton>
      </div>
    </>
  );

  // main render
  return (
    <div className="min-h-screen  flex items-center justify-center p-4">
      <AntdCard className="w-full max-w-md bg-[#141c31]  shadow-2xl border-0 rounded-2xl overflow-hidden">
        <div className="p-10">
          {step === "forgot" && forgotBlock}
          {step === "verify" && verifyBlock}
          {step === "reset" && resetBlock}
        </div>
      </AntdCard>
    </div>
  );
}
